import styled from "@emotion/styled"
import { motion } from "framer-motion"
import { forwardRef, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { cssVars } from "components/nav-v2/Wrapper"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"

import { useUser } from "contexts/User"
import getMediaQuery from "css/breakpoints"
import { buttonFilled, buttonReset, opaqueGreen, opaqueWhite } from "css/buttons"
import { legend100, normal } from "css/text"

import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { fullGrid } from "css/grid"

const Root = styled.div`
  grid-column: col 1 / span 6;
  grid-row: 3;
  align-self: start;
  margin-block-start: 5px;

  ${getMediaQuery("m")} {
    margin-block-start: unset;
    grid-column: col 6 / span 5;
    grid-row: 1;
    align-self: center;
  }
`

const Container = styled.div`
  display: flex;

  > span {
    width: 100%;
    justify-self: start;
    text-align: start;
    padding-block: 0 4px;
    display: flex;
    align-items: center;
    /* font-size: 14px; */
    border-bottom: solid 1px rgb(100, 100, 100);

    ${legend100}
    ${getMediaQuery("m")} {
      /* font-size: 16px; */
      /* padding-block: 8px; */
    }

    span {
      margin-inline-start: auto;
      ::after {
        background: currentColor;
        content: "";
        display: block;
        height: 1em;
        mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path d="M7.5,11L0.9,4H7h7L7.5,11z"/></svg>');
        pointer-events: none;
        width: 1em;
        transition: background-color 0.3s ease-out;
      }
    }

    &:not(:has(span)):hover {
      cursor: default;
    }
  }
`

const Button = styled.button`
  ${buttonReset}
  width: 100%;
  justify-self: start;
  text-align: start;
  padding-block: 0 4px;
  display: flex;
  align-items: center;
  /* font-size: 14px; */
  border-bottom: solid 1px rgb(100, 100, 100);

  ${legend100}
  ${getMediaQuery("m")} {
    /* font-size: 16px; */
    /* padding-block: 8px; */
  }

  span {
    margin-inline-start: auto;
    ::after {
      background: currentColor;
      content: "";
      display: block;
      height: 1em;
      mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path d="M7.5,11L0.9,4H7h7L7.5,11z"/></svg>');
      pointer-events: none;
      width: 1em;
      transition: background-color 0.3s ease-out;
    }
  }

  &:not(:has(span)):hover {
    cursor: default;
  }
`

const Confirm = styled.a`
  ${buttonFilled}
  ${opaqueGreen}
  ${legend100}
  height: 1.875rem;
  margin-inline-start: 2ex;
  padding-inline: 1.5625rem;
`

const PaneCont = styled(motion.div)`
  position: fixed;
  background: rgb(var(--light-grey));
  z-index: 10;
  padding-block: 20px;
  // margin-top: 10px;
  inset-block-start: var(${cssVars.main.alert.height});
  inset-inline: 0;
  width: 100vw;
  height: calc(100vh - var(${cssVars.main.alert.height}));
  ${fullGrid}

  ${getMediaQuery("m")} {
    position: absolute;
    margin-top: 10px;
    display: block;
    width: auto;
    height: auto;
    width: calc(var(--grid-col-unit) * 5 + var(--grid-gap) * 4);
    padding-inline-start: calc(var(--grid-col-unit));
    /* padding-inline-end: 10px; */
    border-radius: 8px;
    inset-inline-start: unset;
    inset-inline-end: 22%;
    inset-block-start: 65%;
    filter: drop-shadow(0px 2px 30px rgba(0 0 0 / 0.4));
  }
`

const Pane = styled(motion.ul)`
  grid-column: main;
  column-count: 2;
  ${getMediaQuery("m")} {
    padding-block-end: 20px;
  }
  /* padding-inline: calc(var(--outer-margin)); */
`

const Item = styled.li`
  padding-block: 6px;
  padding-inline-end: 20px;
`

const Link = styled.a`
  /* padding-inline: 40px; */

  position: relative;
  ${legend100}
  ${normal}

  font-size: 16px;

  text-decoration: none;
  transition: color 0.3s;
  will-change: color;
  color: rgb(var(--light-black));

  /* white-space: nowrap; */

  &.active {
    color: rgb(var(--green));

    &::after {
      content: "";
      position: relative;
      display: inline-block;
      background-color: currentColor;
      border-radius: 50%;
      width: 0.5rem;
      height: 0.5rem;
      margin-inline-start: 0.5rem;
      margin-block-end: 0.1rem;
    }
  }

  @media (hover: hover) {
    &:hover {
      color: rgb(var(--green));
    }
  }
`

const LangPane = forwardRef(({ onClose, statut, setStatut, targetLocale, filteredLocales }, btnRef) => {
  const console = useConsole()
  const env = useEnv()
  const navigation = useNavigation()
  const location = useLocation()
  const user = useUser()

  const locale = useLocale()

  const ref = useRef()
  const currentFocusRef = useRef()

  function setLocaleAndNavigate(e) {
    waTriggerEvent({
      eventName: "BannerLanguageClicked",
      eventType: "Click",
      bannerLanguage: {
        language: e.target.getAttribute("lang"),
      },
    })

    e.preventDefault()
    user.locale = e.target.getAttribute("lang")
    requestAnimationFrame(() => {
      const targetLocale = locale.locales.find(locale => locale.codes.www === e.target.getAttribute("lang"))
      if (
        (targetLocale.codes.country && targetLocale.codes.country !== user.countryCode) ||
        (!targetLocale.codes.country && user.countryCode !== user.realCountryCode)
      )
        window.location.href = e.target.href
      else navigation.navigate(e.target.href, { preventLastLocation: true }, { replace: true })
    })
    onClose()
  }

  const variants = {
    closed: { y: 20, opacity: 0, transition: { duration: 0.35, ease: "linear" }, transitionEnd: { visibility: "hidden" } },
    opened: { y: 0, opacity: 1, visibility: "visible", transition: { duration: 0.35, ease: "linear" } },
  }

  function handleNextPrev(n) {
    if (n === "ArrowUp") {
      const node = currentFocusRef.current.parentNode.previousSibling
      node?.firstChild?.focus()
      currentFocusRef.current = document.activeElement
    } else if (n === "ArrowDown") {
      const node = currentFocusRef.current.parentNode.nextSibling
      node?.firstChild?.focus()
      currentFocusRef.current = document.activeElement
    }
  }

  function onKeyDownCapture(e) {
    if (e.key === "Escape") {
      setStatut("closed")
      btnRef.current.focus()
      return
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault()
      handleNextPrev(e.key)
    }
  }

  useEffect(() => {
    const cur = ref.current.querySelector(`[data-www="${targetLocale.codes.www}"]`) // "en" should always be the default
    cur.focus()
    currentFocusRef.current = cur
  }, [])

  const { banners } = locale
  return (
    <PaneCont id='languagesPanel' variants={variants} initial='closed' animate={statut} onKeyDownCapture={onKeyDownCapture}>
      <Pane ref={ref}>
        {filteredLocales.map(locale => {
          let href =
            env.cn && locale.external
              ? new URL(navigation.localize(navigation.slug, locale.codes.www), global.location?.origin ?? `https://${env.hostname}`).href
              : !env.cn && locale.codes.www === "zh-hans-cn"
              ? new URL(navigation.slug, "https://www.rolex.cn").href
              : navigation.localize(navigation.slug, locale.codes.www)
          href += location.search

          const label = locale.codes.country ? locale.label : banners[locale.codes.cms].international.replace("%languages%", locale.label)

          return (
            <Item key={locale.codes.www}>
              <Link
                href={href}
                data-www={locale.codes.www}
                lang={locale.codes.www}
                dir={locale.dir}
                onClick={setLocaleAndNavigate}
                className={locale.codes.www === targetLocale.codes.www ? "active" : null}
                aria-current={locale.codes.www === targetLocale.codes.www || null}
                target='_self'
              >
                {label}
              </Link>
            </Item>
          )
        })}
      </Pane>
    </PaneCont>
  )
})

export default function LangDropDown({ onClose, cta, targetLocale, filteredLocales }) {
  const console = useConsole()
  const navigation = useNavigation()
  const env = useEnv()
  const ref = useRef()
  const btnRef = useRef()
  const [statut, setStatut] = useState("closed")

  const { banners } = useLocale()
  const lab = targetLocale.codes.country ? targetLocale.label : banners[targetLocale.codes.cms].international.replace("%language%", targetLocale.label)

  function onConfirm(e) {
    e.preventDefault()
    const activeLink = document.querySelector("#languagesPanel .active")
    if (activeLink) {
      activeLink.click()
    }
    onClose()
  }

  function onClick() {
    if (filteredLocales.length <= 1) return
    if (statut === "closed") {
      setStatut("opened")
    } else {
      setStatut("closed")
    }
  }

  function onHeaderUpdate(e) {
    if (e.t === 0) {
      if (statut === "opened") {
        setStatut("closed")
      }
    }
  }

  function onBlur(e) {
    if (!ref.current.contains(e.relatedTarget)) {
      setStatut("closed")
    }
  }

  const toggleVariants = {
    closed: { rotate: 0, transition: { duration: 0.35, ease: "linear" } },
    opened: { rotate: 180, transition: { duration: 0.35, ease: "linear" } },
  }

  return (
    <Root ref={ref} onBlur={onBlur}>
      <Container>
        {filteredLocales.length <= 1 ? (
          <span id='selectedCountry' aria-describedby='languageBannerText'>
            {lab}
            {filteredLocales.length > 1 && <motion.span variants={toggleVariants} initial='closed' animate={statut} />}
          </span>
        ) : (
          <Button
            ref={btnRef}
            type='button'
            id='selectedCountry'
            aria-describedby='languageBannerText'
            aria-controls='languagesPanel'
            aria-expanded={statut === "opened"}
            onClick={onClick}
          >
            {lab}
            {filteredLocales.length > 1 && <motion.span variants={toggleVariants} initial='closed' animate={statut} />}
          </Button>
        )}
        <LangPane ref={btnRef} onClose={onClose} statut={statut} setStatut={setStatut} targetLocale={targetLocale} filteredLocales={filteredLocales} />
        <Confirm
          href={new URL(navigation.localize(navigation.slug, targetLocale.codes.www), global.location?.origin ?? `https://${env.hostname}`)}
          onClick={onConfirm}
          target='_self'
          aria-labelledby='languageBannerText selectedCountry'
        >
          {cta}
        </Confirm>
      </Container>
      {/* <AnimatePresence>{statut === "opened" ? <LangPane ref={btnRef} onClose={onClose} setStatut={setStatut} /> : null}</AnimatePresence> */}
    </Root>
  )
}
